import React, { useState, useEffect, useRef } from "react";
import { Button, Input, Space, Table, message } from "antd";
import { ExpandAltOutlined, SearchOutlined } from "@ant-design/icons";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Highlighter from "react-highlight-words";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import BannedOtpCountTable from "./bannedOtpCountTable";
import "./allTestersBannedAppsOtpCountV2.css";

const apiUrl = process.env.REACT_APP_API_AllTestersBannedAppOTPCount;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AllTestersBannedAppsOtpCountV2 = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [testers, setTesters] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const [searchedColumn, setSearchedColumn] = useState("");
  const [selectedTester, setSelectedTester] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const searchInput = useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const handleViewClick = (record) => {
    setSelectedTester(record);
    setOpen(true);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      setTesters(Array.isArray(data) && data.length > 0 ? data : []);
    } catch (error) {
      console.error("Error fetching banned apps OTP count:", error);
      messageApi.error("Failed to fetch banned apps OTP count");
      setTesters([]);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div className="banned-apps-v2-filter" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
        />
        <Space className="banned-apps-v2-filter-actions">
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined className={filtered ? "banned-apps-v2-filter-active" : ""} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex]?.toString() || "").toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "rgba(56, 189, 248, 0.3)", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text != null ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: "Tester Name",
      dataIndex: "first_name",
      key: "first_name",
      width: "40%",
      ...getColumnSearchProps("first_name"),
      sorter: (a, b) => (a.first_name || "").localeCompare(b.first_name || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Total OTP Count from Banned Apps",
      dataIndex: "otps_count",
      key: "otps_count",
      width: "35%",
      ...getColumnSearchProps("otps_count"),
      sorter: (a, b) => (a.otps_count || 0) - (b.otps_count || 0),
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "descend",
    },
    {
      title: "Actions",
      key: "actions",
      width: "25%",
      render: (_, record) => (
        <Button
          type="default"
          icon={<ExpandAltOutlined />}
          onClick={() => handleViewClick(record)}
          className="banned-apps-v2-btn-view"
        >
          View Details
        </Button>
      ),
    },
  ];

  const isEmpty = hasFetched && !loading && (!testers || testers.length === 0);

  return (
    <div className="banned-apps-v2">
      {contextHolder}
      <div className="banned-apps-v2-header">
        <h1 className="banned-apps-v2-title">Banned App OTP Summary</h1>
      </div>

      {isEmpty ? (
        <div className="banned-apps-v2-empty">
          <p className="banned-apps-v2-empty-text">No data available.</p>
        </div>
      ) : (
        <div className="banned-apps-v2-card">
          <Table
            columns={columns}
            dataSource={testers}
            rowKey={(r, idx) => r.id || `${r.first_name || ""}-${idx}`}
            loading={loading}
            locale={{ emptyText: loading ? "Loading..." : "No data" }}
            pagination={{
              showSizeChanger: true,
              defaultPageSize: 10,
              pageSizeOptions: ["5", "10", "20", "50"],
              showTotal: (total) => `Total ${total} testers`,
            }}
            className="banned-apps-v2-table"
            scroll={{ x: "max-content" }}
          />
        </div>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
        className="banned-apps-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <AppBar className="banned-apps-v2-appbar" position="relative">
          <Toolbar className="banned-apps-v2-toolbar">
            <Typography variant="h6" component="div" className="banned-apps-v2-dialog-title">
              Banned App OTP Count
            </Typography>
            {selectedTester && (
              <Typography variant="body2" className="banned-apps-v2-tester-name">
                {selectedTester.first_name}
              </Typography>
            )}
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setOpen(false)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <div className="banned-apps-v2-body">
          {selectedTester && <BannedOtpCountTable testerId={selectedTester.id} />}
        </div>
      </Dialog>
    </div>
  );
};

export default AllTestersBannedAppsOtpCountV2;
